import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CommonGlobalSettingsService, RootState } from '@umanforge/common';
import InfoTooltip from '@common_components/others/infoTooltip.component';
import ValidationIndicator from '@common_components/others/validationIndicator.component';
import { useTranslation } from 'react-i18next';
import ImageWithError from 'components/other/_ImageWithError';
import { DEFAULT_VALUES } from 'constants/calendar.constants';
import ImageOnError from 'components/other/ImageOnError';
import { Tooltip } from 'react-tooltip';

interface InputImageProps {
  className?: string;
  label?: string;
  tooltip?: string;
  value?: string;
  textSize?: string;
  onChange?: ( value: string | File ) => void;
  disable?: boolean;
  isRequired?: boolean;
  labelPosition?: 'top' | 'left';
  maxSizeMB?: number;
}

const InputImage: React.FC<InputImageProps> = ( {
  className = 'w-full',
  label,
  isRequired = false,
  tooltip,
  textSize = 'w-3/12 mr-4',
  disable = false,
  value,
  onChange,
  labelPosition = 'left',
  maxSizeMB = DEFAULT_VALUES.MAX_FILE_SIZE
} ) => {
  const [ errorMessage, setErrorMessage ] = useState<string | undefined>();
  const [ fileName, setFileName ] = useState<string | undefined>();
  const [ fileSizeMB, setFileSizeMB ] = useState<number | undefined>();
  const [ maxFileSize, setMaxFileSize ] = useState<number>( maxSizeMB );
  const [ previewUrl, setPreviewUrl ] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>( null );
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );

  useEffect( () => {
    async function fetchMaxFileSize() {
      if ( token && dispatch ) {
        const size = await CommonGlobalSettingsService.getMaxFileSize( token, dispatch );
        if ( size && !isNaN( size ) ) {
          setMaxFileSize( Number( size ) / 1000 );
        }
      }
    }
    fetchMaxFileSize();
  }, [ token, dispatch ] );

  useEffect( () => {
    if ( !value && !previewUrl ) {
      setFileName( undefined );
      setFileSizeMB( undefined );
      setPreviewUrl( undefined );
    }
    else if ( typeof value === 'string' && !previewUrl ) {
      setPreviewUrl( undefined );
    }
  }, [ value ] );

  const resetFileState = () => {
    setFileName( undefined );
    setFileSizeMB( undefined );
    setPreviewUrl( undefined );
    if ( inputRef.current ) {
      inputRef.current.value = '';
    }
  };

  const handleFileChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    const file = e.target.files?.[ 0 ];
    if ( !file ) {return;}

    if ( !file.type.startsWith( 'image/' ) ) {
      setErrorMessage( t( 'translation:errors.not_an_image' ) );
      return resetFileState();
    }

    setErrorMessage( undefined );

    if ( file.size > ( maxFileSize * 1024 * 1024 ) ) {
      setErrorMessage(  `${ t( 'translation:errors.file_too_large' ) } ${ t( 'translation:errors.max_size_is' ) } ${ maxFileSize }${ t( 'translation:MB' )}` );
      return resetFileState();
    }

    setPreviewUrl( URL.createObjectURL( file ) );
    onChange?.( file );

    setFileName( file.name );
    setFileSizeMB( Number( ( file.size / 1024 / 1024 ).toFixed( 2 ) ) );
  };

  const isFlexLayout = labelPosition === 'left';

  const UploadIcon = () => {
    return (
      <svg className="w-7 h-7 p-px" viewBox="0 0 24 24" fill="none">
        <path d="M13 4H8.8C7.11984 4 6.27976 4 5.63803 4.32698C5.07354 4.6146 4.6146 5.07354 4.32698 5.63803C4 6.27976 4 7.11984 4 8.8V15.2C4 16.8802 4 17.7202 4.32698 18.362C4.6146 18.9265 5.07354 19.3854 5.63803 19.673C6.27976 20 7.11984 20 8.8 20H15.2C16.8802 20 17.7202 20 18.362 19.673C18.9265 19.3854 19.3854 18.9265 19.673 18.362C20 17.7202 20 16.8802 20 15.2V11" stroke="#777" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M4 16L8.29289 11.7071C8.68342 11.3166 9.31658 11.3166 9.70711 11.7071L13 15M13 15L15.7929 12.2071C16.1834 11.8166 16.8166 11.8166 17.2071 12.2071L20 15M13 15L15.25 17.25" stroke="#777" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M18 8V3M18 3L16 5M18 3L20 5" stroke="#777" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  };

  return (
    <div className={`rounded-md ${className}`}>
      <div className={isFlexLayout ? 'flex' : ''}>
        {label && (
          <div className={`flex gap-2 relative items-center ${textSize}`}>
            <label className={`text-gray-600 text-wrap break-words ${isFlexLayout ? 'flex items-center' : ''}`}>
              {label}
            </label>
            {tooltip && <InfoTooltip label={label} tooltip={tooltip} />}
          </div>
        )}


        <div className={`relative w-full ${labelPosition === 'top' ? 'mt-2' : ''} flex items-center`}>
          {disable ? (
            <ImageWithError url={typeof value === 'string' ? value : undefined} size={14} />
          ) : (
            <>
              <ValidationIndicator isRequired={isRequired} isValid={isRequired} />
              <label
                htmlFor="upload-icon"
                className="modal-input select-none relative text-center bg-white-base h-16 w-full border border-gray-200 cursor-pointer"
                style={{ color: '#9090AA' }}
              >
                {( previewUrl || value ) ? (
                  <div className="flex flex-col justify-center items-center w-full">
                    { previewUrl ?
                      (
                        ( previewUrl.split( ':' )[ 0 ] == 'blob' ) ?
                          <ImageOnError src={ previewUrl } className='h-14 w-14 mt-1' /> :
                          <ImageWithError url={typeof previewUrl === 'string' ? previewUrl : undefined} size={14} />
                      ) :
                      ( value != null ) && (
                        ( value.split( ':' )[ 0 ] == 'blob' ) ?
                          <ImageOnError src={ value } className='h-14 w-14 mt-1' /> :
                          <ImageWithError url={typeof value === 'string' ? value : undefined} size={14} />
                      )
                    }
                  </div>
                ) : (
                  <div className="flex flex-col justify-center items-center w-full h-full">
                    <div className='inline-flex'>
                      <p className="text-center">{t( 'translation:choose_file' )}</p>
                      <span
                        id={'role-info-tooltip'}
                        className={'ml-2 cursor-pointer flex items-center justify-center rounded-full bg-gray-200/60 w-6 h-6 text-gray-700 text-sm font-bold'}
                      > i </span>
                      <Tooltip
                        anchorSelect={'#role-info-tooltip'}
                        className={'z-[1000] max-w-[400px] text-wrap'}
                        place="top"
                        positionStrategy='fixed'
                      >
                        <span>
                          <span className="text-[14px] font-semibold underline underline-offset-2">{ t( 'translation:image_format' ) }</span>
                          <p className="pl-2 pt-1">{ t( '.xbm, .tif, .jfif, .pjp, .apng, .jpeg, .heif, .ico, .tiff, .webp, .svgz, .jpg, .heic, .gif, .svg, .png, .bmp, .pjpeg, .avif' ) }</p>
                        </span>
                      </Tooltip>
                    </div>
                    <span className="text-xs text-gray-400 mt-1">
                      { `${ t( 'translation:max' ) }: ${ maxFileSize }${ t( 'translation:MB' )}` }
                    </span>
                  </div>
                )}
                <div className="absolute w-7 h-7 bg-gray-200 hover:bg-gray-300 right-1 bottom-1 rounded">
                  <UploadIcon />
                </div>
              </label>
            </>
          )}
          <input
            type="file"
            ref={inputRef}
            accept="image/*"
            id="upload-icon"
            className="opacity-0 -z-10 h-0 w-0 absolute"
            onChange={handleFileChange}
          />
        </div>
      </div>

      {/* Affichage du nom du fichier et de la taille */}
      {fileName && fileSizeMB !== undefined && (
        <div className="mt-2 text-sm text-gray-600 flex items-center">
          <span className="truncate">
            { `${fileName} - ${fileSizeMB}${ t( 'translation:MB' )} / ${maxFileSize}${ t( 'translation:MB' )}` }
          </span>
        </div>
      )}

      {errorMessage && (
        <div className="mt-2 text-sm text-red-600 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default InputImage;
